import { useState, useEffect, useCallback } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKInput from "components/MKInput";
import MKButton from "components/MKButton";
import MKAlert from "components/MKAlert";
import SEO from "components/SEO";

// Admin sections
import AdminBookings from "pages/Admin/sections/AdminBookings";
import AdminCalendar from "pages/Admin/sections/AdminCalendar";
import AdminPricing from "pages/Admin/sections/AdminPricing";

// API
import { adminLogin } from "api";

const TOKEN_KEY = "bh_admin_token";

function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const handleLogin = useCallback(async () => {
    if (!password) return;
    setLoggingIn(true);
    setLoginError(null);
    try {
      const { token: newToken } = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
      setPassword("");
    } catch (err) {
      setLoginError(err.message || "Login failed");
    } finally {
      setLoggingIn(false);
    }
  }, [password]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  // Handle expired tokens from child components
  const handleAuthError = useCallback(() => {
    handleLogout();
    setLoginError("Session expired. Please log in again.");
  }, [handleLogout]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !token) handleLogin();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleLogin, token]);

  if (!token) {
    return (
      <MKBox minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
        <SEO noindex title="Owner Login" path="/admin" />
        <Card sx={{ p: 4, maxWidth: 400, width: "100%" }}>
          <MKTypography variant="h4" textAlign="center" mb={1}>
            <Icon sx={{ mr: 1, verticalAlign: "middle" }}>admin_panel_settings</Icon>
            Owner Login
          </MKTypography>
          <MKTypography variant="body2" color="dark" fontWeight="bold" textAlign="center" mb={3}>
            Enter your admin password to manage bookings and calendar.
          </MKTypography>
          {loginError && (
            <MKAlert color="error" dismissible sx={{ mb: 2 }}>
              {loginError}
            </MKAlert>
          )}
          <MKInput
            type="password"
            label="Password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 3 }}
          />
          <MKButton
            variant="gradient"
            color="info"
            fullWidth
            disabled={!password || loggingIn}
            onClick={handleLogin}
          >
            {loggingIn ? "Logging in..." : "Log In"}
          </MKButton>
        </Card>
      </MKBox>
    );
  }

  return (
    <>
      <SEO noindex title="Owner Dashboard" path="/admin" />
      <MKBox
        minHeight="15vh"
        width="100%"
        variant="gradient"
        bgColor="dark"
        sx={{ display: "grid", placeItems: "center" }}
      >
        <Container>
          <MKBox display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <MKTypography variant="h4" color="white">
              <Icon sx={{ mr: 1, verticalAlign: "middle" }}>admin_panel_settings</Icon>
              Owner Dashboard
            </MKTypography>
            <MKButton variant="outlined" color="white" size="small" onClick={handleLogout}>
              <Icon sx={{ mr: 0.5 }}>logout</Icon>
              Log Out
            </MKButton>
          </MKBox>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 1, lg: 3 },
          mt: -4,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <Container>
          <MKBox sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
              <Tab icon={<Icon>list_alt</Icon>} label="Bookings" />
              <Tab icon={<Icon>calendar_month</Icon>} label="Calendar" />
              <Tab icon={<Icon>attach_money</Icon>} label="Pricing" />
            </Tabs>
          </MKBox>
          {activeTab === 0 && <AdminBookings token={token} onAuthError={handleAuthError} />}
          {activeTab === 1 && <AdminCalendar token={token} onAuthError={handleAuthError} />}
          {activeTab === 2 && <AdminPricing token={token} onAuthError={handleAuthError} />}
        </Container>
      </Card>
    </>
  );
}

export default Admin;
